// page-notes-web/src/components/Modals/TagPicker.jsx
import { useState, useRef, useEffect } from 'react';
import { useTags } from '../../contexts/TagsContext';
import './TagPicker.css';

export default function TagPicker({ value = [], onChange, disabled = false }) {
  const { tags, loading, createTag, getTagById } = useTags();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedTags = value.map(id => getTagById(id)).filter(Boolean);
  
  const filteredTags = tags.filter(tag => 
    tag.name.toLowerCase().includes(search.toLowerCase()) &&
    !value.includes(tag.id)
  );

  const handleToggle = (tagId) => {
    if (value.includes(tagId)) {
      onChange(value.filter(id => id !== tagId));
    } else {
      onChange([...value, tagId]);
    }
  };

  const handleRemoveTag = (tagId, e) => {
    e.stopPropagation();
    onChange(value.filter(id => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!search.trim()) return;
    
    try {
      const newTag = await createTag({ name: search.trim(), color: '#ffaa00' });
      if (newTag) {
        onChange([...value, newTag.id]);
        setSearch('');
      }
    } catch (err) {
      console.error('Failed to create tag:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim() && filteredTags.length === 0) {
      e.preventDefault();
      handleCreateTag();
    }
  };

  return (
    <div className="tag-picker" ref={containerRef}>
      {/* Selected tags */}
      <div 
        className={`picker-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        {selectedTags.length > 0 ? (
          <div className="selected-tags">
            {selectedTags.map(tag => (
              <span 
                key={tag.id}
                className="selected-tag"
                style={{ 
                  backgroundColor: `${tag.color}22`,
                  borderColor: tag.color,
                  color: tag.color
                }}
              >
                {tag.name}
                <button 
                  type="button"
                  onClick={(e) => handleRemoveTag(tag.id, e)}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="placeholder">Add tags...</span>
        )}
      </div>

      {isOpen && (
        <div className="picker-dropdown">
          <div className="picker-search">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search or create tag..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="picker-options">
            {loading ? (
              <div className="picker-loading">Loading tags...</div>
            ) : filteredTags.length === 0 && search.trim() ? (
              <button
                type="button"
                className="picker-create"
                onClick={handleCreateTag}
              >
                <span>Create "{search.trim()}"</span>
                <span className="create-hint">Press Enter</span>
              </button>
            ) : filteredTags.length === 0 ? (
              <div className="picker-empty">
                {tags.length === 0 ? 'No tags created yet' : 'All tags selected'}
              </div>
            ) : (
              filteredTags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  className="picker-option"
                  onClick={() => handleToggle(tag.id)}
                >
                  <span 
                    className="option-dot"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="option-text">{tag.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}